import {
  AddCreditCardToPaymentRequestActionInput,
  AddCreditCardToPaymentRequestActionOutput,
  CreateStripeCustomerPortalUrlActionOutput,
  CreateStripeSubscriptionActionInput,
  CreateStripeSubscriptionActionOutput,
  GetPaymentRequestActionInput,
  GetPaymentRequestActionOutput,
  GetPaymentRequestsActionInput,
  GetPaymentRequestsActionOutput,
  GetStripeConfigActionInput,
  GetStripeConfigActionOutput,
  GetTripUsersPaymentMethodsInput,
  GetTripUsersPaymentMethodsOutput,
  GetUserCreditCardInput,
  GetUserCreditCardOutput,
  GetUserCreditCardsOutput,
  ICreditCard,
  InvalidatePaymentRequestsActionInput,
  InvalidatePaymentRequestsActionOutput,
  PaymentRequest,
  PaymentTransaction,
  RevealCreditCardInput,
  RevealCreditCardOutput,
  SaveUserCreditCardOutput,
  SaveUserCreditCardSecuredInput,
  SendPaymentRequestActionInput,
  SendPaymentRequestActionOutput,
  SetStripeDefaultPaymentMethodActionInput,
  SetStripeDefaultPaymentMethodActionOutput,
  TokenizedCreditCard,
  TripUserPaymentMethod,
} from "@cruisen/contract"
import { _t } from "@cruisen/i18n"
import { callFirebaseFunction } from "../callFirebaseFunction.ts"
import { callFirebaseHttpFunction } from "../callFirebaseHttpFunction.ts"

export const getUserCreditCard = async (
  input: GetUserCreditCardInput = {},
): Promise<TokenizedCreditCard | null> => {
  const response = await callFirebaseFunction({
    name: "billingAction",
    payload: { action: "getUserCreditCard", ...input },
  })

  if (response.error) {
    alert(`${_t("errors.billing")} ${_t("errors.tryAgainLater")}`)
    return null
  }

  return (response.data as GetUserCreditCardOutput).result
}

export const getTripUsersPaymentMethods = async (
  input: GetTripUsersPaymentMethodsInput,
): Promise<TripUserPaymentMethod[]> => {
  const response = await callFirebaseFunction({
    name: "billingAction",
    payload: { action: "getTripUsersPaymentMethods", ...input },
  })

  if (response.error) {
    alert(`${_t("errors.billing")} ${_t("errors.tryAgainLater")}`)
    return null
  }

  return (response.data as GetTripUsersPaymentMethodsOutput).result
}

export const revealCreditCard = async (
  input: RevealCreditCardInput,
): Promise<ICreditCard | null> => {
  const response = await callFirebaseHttpFunction({
    name: "billingRevealCreditCard",
    payload: input,
  })

  if (response.error) {
    alert(`${_t("errors.billing")} ${_t("errors.tryAgainLater")}`)
    return null
  }

  return (response.data as RevealCreditCardOutput)?.result?.creditCard ?? null
}

/**
 * await sendPaymentRequest({
 *   paymentRequest: {
 *     tripId: activeTrip.id,
 *     message: "This is payment request",
 *     assignedTo: [loggedInUser.id],
 *     lineItems: [
 *       {
 *         type: "cabinBooking",
 *         tripRoomId: store.currentRoomId,
 *         currency: "USD",
 *         amount: "100.99",
 *         fees: "50",
 *         taxes: "20",
 *         total: "170.99",
 *         totalDueDate: new Date("2025-10-30"),
 *         deposit: "10",
 *         description: "Hello World",
 *         label: "Cabin book",
 *         meta: {
 *           cabinGradeName: "Test",
 *           cabinGradeType: "outside",
 *           cabinNo: "",
 *           cruiseSailingId: 0,
 *           cruiseShipId: 0,
 *           deckId: 0,
 *           deckName: "Test",
 *           itineraryCode: "TST",
 *           reservationCode: "123",
 *         },
 *       },
 *     ],
 *   },
 * })
 *
 * @param {SendPaymentRequestActionInput} input
 * @return {Promise<PaymentRequest>}
 */
export const sendPaymentRequest = async (
  input: SendPaymentRequestActionInput,
): Promise<PaymentRequest> => {
  const response = await callFirebaseFunction({
    name: "billingAction",
    payload: { action: "sendPaymentRequest", ...input },
  })

  if (response.error) {
    alert(`${_t("errors.billing")} ${_t("errors.tryAgainLater")}`)
    return null
  }

  return (response.data as SendPaymentRequestActionOutput)?.result
}

/**
 * Get a single payment request by its ID, e.g. when user wants to view a payment request from notification or navigator message.
 *
 * await getPaymentRequest({
 *   paymentRequestId: "123",
 * })
 *
 * @param {GetPaymentRequestActionInput} input
 * @return {Promise<PaymentRequest>}
 */
export const getPaymentRequest = async (
  input: GetPaymentRequestActionInput,
): Promise<PaymentRequest | null> => {
  const response = await callFirebaseFunction({
    name: "billingAction",
    payload: { action: "getPaymentRequest", ...input },
  })

  if (response.error) {
    alert(`${_t("errors.billing")} ${_t("errors.tryAgainLater")}`)
    return null
  }

  return (response.data as GetPaymentRequestActionOutput)?.result
}

/**
 * Get payment requests for a trip, e.g. when user wants to view all payment requests for a trip.
 *
 * await getPaymentRequests({
 *   tripId: "123",
 * })
 *
 * @param {GetPaymentRequestsActionInput} input
 * @return {Promise<PaymentRequest[]>}
 */
export const getPaymentRequests = async (
  input: GetPaymentRequestsActionInput,
): Promise<PaymentRequest[]> => {
  const response = await callFirebaseFunction({
    name: "billingAction",
    payload: { action: "getPaymentRequests", ...input },
  })

  if (response.error) {
    alert(`${_t("errors.billing")} ${_t("errors.tryAgainLater")}`)
    return null
  }

  return (response.data as GetPaymentRequestsActionOutput)?.result
}

/**
 * Invalidates payment requests for a trip. If called by an agent, marks requests as cancelled.
 * If called by a user, marks them as rejected.
 *
 * await invalidatePaymentRequests({
 *   tripId: "123"
 * })
 *
 * @param {InvalidatePaymentRequestsActionInput} input - Input containing tripId
 * @return {Promise<InvalidatePaymentRequestsActionOutput>} Success status and code
 */
export const invalidatePaymentRequests = async (
  input: InvalidatePaymentRequestsActionInput,
): Promise<InvalidatePaymentRequestsActionOutput> => {
  const response = await callFirebaseFunction({
    name: "billingAction",
    payload: { action: "invalidatePaymentRequests", ...input },
  })

  if (response.error) {
    alert(`${_t("errors.billing")} ${_t("errors.tryAgainLater")}`)
    return null
  }

  return response.data as InvalidatePaymentRequestsActionOutput
}

export const getStripeExternalSubscriptionManageUrl = async (): Promise<string | null> => {
  const createPortalLinkResult = await callFirebaseFunction({
    name: "billingAction",
    payload: {
      action: "createStripeCustomerPortalUrl",
      returnUrl: window.location.origin,
    },
  })

  const responseData = createPortalLinkResult.data as CreateStripeCustomerPortalUrlActionOutput

  return responseData?.result?.url
}

export const getStripeConfig = async (
  input?: GetStripeConfigActionInput,
): Promise<GetStripeConfigActionOutput["result"]> => {
  const response = await callFirebaseFunction({
    name: "billingAction",
    payload: {
      action: "getStripeConfig",
      ...(input ?? {}),
    },
  })

  if (response.error) {
    return null
  }

  return (response.data as GetStripeConfigActionOutput).result
}

export const createStripeSubscription = async (
  input: CreateStripeSubscriptionActionInput,
): Promise<CreateStripeSubscriptionActionOutput["result"]> => {
  const response = await callFirebaseFunction({
    name: "billingAction",
    payload: { action: "createStripeSubscription", ...input },
  })

  if (response.error) {
    alert(`${_t("errors.billing")} ${_t("errors.tryAgainLater")}`)
    return null
  }

  return (response.data as CreateStripeSubscriptionActionOutput).result
}

export const setStripeDefaultPaymentMethod = async (
  input: SetStripeDefaultPaymentMethodActionInput,
): Promise<SetStripeDefaultPaymentMethodActionOutput> => {
  const response = await callFirebaseFunction({
    name: "billingAction",
    payload: { action: "setStripeDefaultPaymentMethod", ...input },
  })

  if (response.error) {
    alert(`${_t("errors.billing")} ${_t("errors.tryAgainLater")}`)
    return null
  }

  return response.data as SetStripeDefaultPaymentMethodActionOutput
}

/**
 * Save a credit card for a user.
 *
 * @param {SaveUserCreditCardSecuredInput} input
 * @return {Promise<TokenizedCreditCard>}
 */
export const saveUserCreditCard = async (
  input: SaveUserCreditCardSecuredInput,
): Promise<TokenizedCreditCard> => {
  const response = await callFirebaseHttpFunction({
    name: "billingSaveCreditCard",
    payload: input,
  })

  if (response.error) {
    console.error(`${_t("errors.billing")} ${_t("errors.tryAgainLater")}`)
    return null
  }

  return (response.data as SaveUserCreditCardOutput).result
}

/**
 * Add a credit card to a payment request.
 *
 * @param {AddCreditCardToPaymentRequestActionInput} input
 * @return {Promise<PaymentTransaction>}
 */
export const addCreditCardToPaymentRequest = async (
  input: AddCreditCardToPaymentRequestActionInput,
): Promise<PaymentTransaction> => {
  const response = await callFirebaseFunction({
    name: "billingAction",
    payload: { action: "addCreditCardToPaymentRequest", ...input },
  })

  if (response.error) {
    console.error(`${_t("errors.billing")} ${_t("errors.tryAgainLater")}`)
    return null
  }

  return (response.data as AddCreditCardToPaymentRequestActionOutput).result
}

export const getUserCreditCards = async (): Promise<TokenizedCreditCard[]> => {
  const response = await callFirebaseFunction({
    name: "billingAction",
    payload: { action: "getUserCreditCards" },
  })

  if (response.error) {
    alert(`${_t("errors.billing")} ${_t("errors.tryAgainLater")}`)
    return null
  }

  return (response.data as GetUserCreditCardsOutput).result
}
